import { Component, OnInit, OnDestroy, HostListener, ChangeDetectorRef, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { SidebarStateService } from '../../services/sidebar-state.service';
import { AuthService } from '../../../core/services/auth/auth.service';
import { NotificationService } from '../../../core/services/notifications/notification.service';
import { NotificationItem } from '../../../core/models/notifications/notification.models';
import { SupportChatService } from '../../../features/hr_link/hr-support-messages/service/support-chat.service';
import {
  SupportRealtimeService,
  SupportConversationCreatedEvent,
  SupportMessageEvent,
} from '../../../features/hr_link/hr-support-messages/service/support-realtime.service';

@Component({
  selector: 'app-top-bar',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './top-bar.component.html',
  styleUrl: './top-bar.component.css'
})
export class TopBarComponent implements OnInit, OnDestroy {
  isSidebarCollapsed = false;
  isNotificationOpen = false;
  private sidebarSubscription?: Subscription;
  private unreadCountSubscription?: Subscription;
  private supportRealtimeSubs: Subscription[] = [];

  private companyId = 0;
  private nextLocalNotificationId = -1;
  private localSupportUnread = 0;
  private lastServerUnread = 0;
  private readonly shownSupportMessageIds = new Set<number>();
  private readonly conversationEmployeeNames = new Map<number, string>();

  notifications: NotificationItem[] = [];
  unreadCount = 0;
  isLoadingNotifications = false;

  private supportUnreadRefreshTimer?: ReturnType<typeof setTimeout>;

  currentPage = 1;
  totalPages = 1;
  readonly pageSize = 20;

  constructor(
    private sidebarStateService: SidebarStateService,
    private authService: AuthService,
    private notificationService: NotificationService,
    private supportChatService: SupportChatService,
    private supportRealtime: SupportRealtimeService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone
  ) {}

  ngOnInit(): void {
    this.sidebarSubscription = this.sidebarStateService.isCollapsed$.subscribe(
      (collapsed) => {
        this.isSidebarCollapsed = collapsed;
      }
    );
    // Keep the local field in sync too (used by *ngIf for "Read All" button).
    this.unreadCountSubscription = this.notificationService.unreadCount$.subscribe(
      (count) => {
        this.ngZone.run(() => {
          this.lastServerUnread = count;
          this.updateDisplayUnreadCount();
        });
      }
    );
    if (this.authService.isLoggedIn()) {
      this.notificationService.startUnreadCountPolling('WEB', 60_000);
      this.setupSupportRealtime();
    }
  }

  ngOnDestroy(): void {
    this.sidebarSubscription?.unsubscribe();
    this.unreadCountSubscription?.unsubscribe();
    if (this.supportUnreadRefreshTimer) clearTimeout(this.supportUnreadRefreshTimer);
    this.supportRealtimeSubs.forEach((s) => s.unsubscribe());
    if (this.companyId > 0) {
      this.supportRealtime.leaveCompany(this.companyId);
    }
    this.notificationService.stopUnreadCountPolling();
  }

  private setupSupportRealtime(): void {
    this.companyId = Number(this.authService.getCompanyId());
    if (!this.companyId) return;

    void this.supportRealtime.joinCompany(this.companyId);
    this.refreshSupportUnreadCount();

    this.supportRealtimeSubs.push(
      this.supportRealtime.conversationCreated$.subscribe((evt) =>
        this.onSupportConversationCreated(evt)
      ),
      this.supportRealtime.messageReceived$.subscribe((evt) =>
        this.onSupportMessageReceived(evt)
      )
    );
  }

  private onSupportConversationCreated(evt: SupportConversationCreatedEvent): void {
    if (Number(evt.companyId) !== this.companyId) return;

    this.conversationEmployeeNames.set(evt.conversationId, evt.name);
    this.addSupportNotification(
      'New HR support request',
      `You have a new message from ${evt.name}`,
      evt.conversationId
    );
    this.scheduleSupportUnreadRefresh();
  }

  private onSupportMessageReceived(evt: SupportMessageEvent): void {
    if (Number(evt.companyId) !== this.companyId) return;
    if (evt.senderType?.toLowerCase() !== 'employee') return;
    if (this.shownSupportMessageIds.has(evt.messageId)) return;

    this.shownSupportMessageIds.add(evt.messageId);

    const employeeName =
      this.conversationEmployeeNames.get(evt.conversationId) ?? evt.senderId;

    this.addSupportNotification(
      'New support message',
      `You have a new message from ${employeeName}`,
      evt.conversationId
    );
    this.scheduleSupportUnreadRefresh();
  }

  private updateDisplayUnreadCount(): void {
    this.unreadCount = this.lastServerUnread + this.localSupportUnread;
    this.cdr.detectChanges();
  }

  private refreshSupportUnreadCount(): void {
    if (!this.companyId) return;
    this.supportChatService
      .getCompanyEmployeeInbox(this.companyId, 1, 500)
      .subscribe({
        next: (res) => {
          if (res?.success && Array.isArray(res.data)) {
            this.localSupportUnread = res.data.reduce(
              (sum, row) => sum + (row.totalUnreadCount || 0),
              0
            );
            this.updateDisplayUnreadCount();
          }
        },
        error: () => {},
      });
  }

  private scheduleSupportUnreadRefresh(): void {
    if (this.supportUnreadRefreshTimer) clearTimeout(this.supportUnreadRefreshTimer);
    this.supportUnreadRefreshTimer = setTimeout(() => this.refreshSupportUnreadCount(), 400);
  }

  private addSupportNotification(
    title: string,
    message: string,
    conversationId: number
  ): void {
    const item: NotificationItem = {
      notificationId: this.nextLocalNotificationId--,
      title,
      message,
      recipientType: 'CompanyAdmin',
      typeKey: 'HR_SUPPORT',
      category: 'HR',
      channel: 'WEB',
      isRead: false,
      readAt: null,
      referenceId: conversationId,
      referenceType: 'SupportConversation',
      metadata: null,
      createdAt: new Date().toISOString(),
    };

    this.ngZone.run(() => {
      this.notifications = [item, ...this.notifications];
      this.localSupportUnread++;
      this.updateDisplayUnreadCount();
    });
  }

  isSupportNotification(notif: NotificationItem): boolean {
    return notif.typeKey === 'HR_SUPPORT';
  }

  onNotificationClick(notif: NotificationItem): void {
    this.markAsRead(notif);

    if (this.isSupportNotification(notif) && notif.referenceId) {
      this.isNotificationOpen = false;
      this.router.navigate(['/hr-link/hr-support-conversation', notif.referenceId], {
        state: { isLoggedIn: true },
      });
    }
  }

  isPayrollEmployer(): boolean {
    return this.authService.getEmployerTypeId() === 2;
  }



  loadNotifications(page: number = this.currentPage): void {
    this.isLoadingNotifications = true;
    this.notificationService
      .getNotifications({ channel: 'WEB', pageNumber: page, pageSize: this.pageSize })
      .subscribe({
        next: (res) => {
          if (res.success && res.data) {
            const localSupport = this.notifications.filter((n) => n.notificationId < 0);
            this.notifications = [...localSupport, ...res.data.items];
            this.currentPage = res.data.pageNumber;
            this.totalPages = res.data.totalPages;
          }
          this.isLoadingNotifications = false;
        },
        error: () => {
          this.isLoadingNotifications = false;
        }
      });
  }

  prevPage(): void {
    if (this.currentPage > 1) {
      this.loadNotifications(this.currentPage - 1);
    }
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.loadNotifications(this.currentPage + 1);
    }
  }

  toggleNotifications(event: Event): void {
    event.stopPropagation();
    this.isNotificationOpen = !this.isNotificationOpen;
    if (this.isNotificationOpen) {
      this.currentPage = 1;
      this.refreshSupportUnreadCount();
      this.loadNotifications(1);
    }
  }

  markAsRead(notification: NotificationItem): void {
    if (notification.isRead) return;

    if (notification.notificationId < 0) {
      if (!notification.isRead) {
        this.localSupportUnread = Math.max(0, this.localSupportUnread - 1);
      }
      notification.isRead = true;
      notification.readAt = new Date().toISOString();
      this.unreadCount = this.lastServerUnread + this.localSupportUnread;
      this.scheduleSupportUnreadRefresh();
      return;
    }

    this.notificationService.markAsRead(notification.notificationId).subscribe({
      next: (res) => {
        if (res.success) {
          notification.isRead = true;
          notification.readAt = new Date().toISOString();
          this.notificationService.refreshUnreadCount('WEB');
        }
      },
      error: () => {}
    });
  }

  markAllAsRead(): void {
    this.notifications.forEach((n) => {
      n.isRead = true;
      n.readAt = new Date().toISOString();
    });
    this.localSupportUnread = 0;
    this.updateDisplayUnreadCount();
    this.notificationService.readAll('WEB').subscribe({
      error: () => {},
    });
  }

  clearNotification(event: Event, notification: NotificationItem): void {
    event.stopPropagation();

    if (notification.notificationId < 0) {
      if (!notification.isRead) {
        this.localSupportUnread = Math.max(0, this.localSupportUnread - 1);
      }
      this.notifications = this.notifications.filter(
        (n) => n.notificationId !== notification.notificationId
      );
      this.unreadCount = this.lastServerUnread + this.localSupportUnread;
      this.scheduleSupportUnreadRefresh();
      return;
    }

    this.notificationService.clearNotification(notification.notificationId).subscribe({
      next: (res) => {
        if (res.success) {
          if (!notification.isRead) {
            this.notificationService.refreshUnreadCount('WEB');
          }
          this.notifications = this.notifications.filter(
            n => n.notificationId !== notification.notificationId
          );
        }
      },
      error: () => {}
    });
  }

  clearAll(): void {
    this.notifications = [];
    this.localSupportUnread = 0;
    this.updateDisplayUnreadCount();
    this.notificationService.clearAll('WEB').subscribe({
      error: () => {},
    });
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.isNotificationOpen = false;
  }
}

